import { motion } from "framer-motion";
import { useRef, useEffect } from "react";
import { Reveal, Overline } from "./Reveal";
import { ChevronLeft, ChevronRight } from "lucide-react";

const experiences = [
  {
    n: "01",
    name: "The Maharaja's Weekend",
    tag: "Heritage · Exclusivity · Private Access",
    body: "Stay within an iconic living palace. A candlelit dinner in a courtyard the public never sees, a classical performance curated for you alone, and a dawn tour of the old city by vintage motorcar.",
    img: "https://images.unsplash.com/photo-1599661046289-e31897846e41",
    alt: "Ornate Indian palace at dusk",
  },
  {
    n: "02",
    name: "Antarctica by Expedition Yacht",
    tag: "Once in a lifetime · Expedition",
    body: "Not a cruise — an expedition. Helicopter excursions over the ice, private polar diving for qualified guests, onboard scientists and photographers, and dinner served amid the icebergs.",
    img: "https://images.unsplash.com/photo-1551986782-d0169b3f8fa7",
    alt: "King penguin in the sub-Antarctic",
  },
  {
    n: "03",
    name: "Monaco Grand Prix by Superyacht",
    tag: "The lifestyle, not the race",
    body: "Anyone can buy a ticket. Almost no one watches Formula 1 from a yacht moored in Monaco harbour — followed by the after-parties and driver appearances few ever attend.",
    img: "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a",
    alt: "Superyacht in a Mediterranean harbour",
  },
  {
    n: "04",
    name: "The Great Migration Safari",
    tag: "Wild · Rare · Utterly private",
    body: "A hot-air balloon at sunrise. Champagne on the plains. A private bush dinner beneath the stars, your own wildlife photographer, and a luxury tented camp at the heart of the migration.",
    img: "https://images.unsplash.com/photo-1516426122078-c23e76319801",
    alt: "Wildlife on the African savannah at sunrise",
  },
  {
    n: "05",
    name: "Secret Italy",
    tag: "Access, after hours",
    body: "A private Tuscan villa. Winemaking beside the estate's owner. After-hours cultural access where it can be arranged, a private opera performance, and a vintage Ferrari through the countryside.",
    img: "https://images.pexels.com/photos/357742/pexels-photo-357742.jpeg",
    alt: "Ripe wine grapes on the vine",
  },
  {
    n: "06",
    name: "The Impossible Celebration",
    tag: "Built entirely around you",
    body: "Not a place — a promise. \u201CI want to celebrate my wife's 40th.\u201D We compose it entirely: a private-island dinner, a concert in the desert, fireworks where permitted, a personal performance, bespoke menus and gifts.",
    img: "https://images.pexels.com/photos/1387577/pexels-photo-1387577.jpeg",
    alt: "Grand fireworks display over the water at night",
  },
];

export const Experiences = () => {
  const scrollerRef = useRef(null);

  const step = () => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const card = el.querySelector("[data-card]");
    return card ? card.offsetWidth + 24 : el.clientWidth * 0.85;
  };

  const advance = (dir = 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 12;
    if (dir === 1 && atEnd) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else if (dir === -1 && el.scrollLeft <= 12) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } else {
      el.scrollBy({ left: dir * step(), behavior: "smooth" });
    }
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let paused = false;
    const pause = () => (paused = true);
    const resume = () => (paused = false);
    el.addEventListener("pointerenter", pause);
    el.addEventListener("pointerleave", resume);
    el.addEventListener("pointerdown", pause);
    el.addEventListener("touchstart", pause, { passive: true });
    const id = setInterval(() => {
      if (!paused) advance(1);
    }, 4200);
    return () => {
      clearInterval(id);
      el.removeEventListener("pointerenter", pause);
      el.removeEventListener("pointerleave", resume);
      el.removeEventListener("pointerdown", pause);
      el.removeEventListener("touchstart", pause);
    };
  }, []);

  return (
    <section
      id="experiences"
      data-testid="experiences-section"
      className="relative bg-ivory py-28 md:py-36 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-16">
          <Reveal>
            <Overline>Beyond the Possible</Overline>
            <h2 className="mt-6 font-serif font-light text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Stories money alone
              <br />
              <span className="italic text-gold">cannot buy.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="max-w-sm">
              <p className="font-sans font-light text-navyAccent/80 text-base leading-relaxed">
                The finest houses do not sell destinations. They compose moments
                you did not know were possible.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  data-testid="experiences-prev"
                  aria-label="Previous experience"
                  onClick={() => advance(-1)}
                  className="h-11 w-11 rounded-full border border-navy/25 text-navy flex items-center justify-center hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  data-testid="experiences-next"
                  aria-label="Next experience"
                  onClick={() => advance(1)}
                  className="h-11 w-11 rounded-full border border-navy/25 text-navy flex items-center justify-center hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Horizontal auto-sliding track (full-bleed) */}
      <div
        ref={scrollerRef}
        data-testid="experiences-scroller"
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 md:px-12 pb-4"
        style={{ scrollbarWidth: "none" }}
      >
        {experiences.map((x) => (
          <motion.article
            key={x.n}
            data-card
            data-testid={`experience-${x.n}`}
            className="snap-start shrink-0 w-[85vw] sm:w-[420px] lg:w-[460px]"
          >
            <div className="group relative overflow-hidden">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={x.img}
                  alt={x.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-navy/10" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
                <span className="pointer-events-none absolute top-3 left-4 font-serif text-5xl text-ivory/90 drop-shadow-[0_2px_16px_rgba(10,17,40,0.6)]">
                  {x.n}
                </span>
              </div>
            </div>
            <div className="pt-6">
              <span className="block text-[0.62rem] tracking-[0.24em] uppercase text-gold font-sans">
                {x.tag}
              </span>
              <h3 className="mt-3 font-serif font-light text-navy text-2xl md:text-3xl leading-tight tracking-tight">
                {x.name}
              </h3>
              <p className="mt-4 font-sans font-light text-navyAccent/80 text-sm leading-relaxed">
                {x.body}
              </p>
              <div className="mt-5 flex items-center gap-3">
                <span className="h-px w-8 bg-gold" />
                <span className="font-serif italic text-navyAccent/55 text-sm">
                  I didn't even know this was possible.
                </span>
              </div>
            </div>
          </motion.article>
        ))}
        {/* trailing spacer so the last card can align left on wide screens */}
        <div className="shrink-0 w-2 md:w-6" aria-hidden="true" />
      </div>
    </section>
  );
};
